import { createContext, useContext, useEffect, useState, useCallback } from "react";
import client from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("truck_token");
    // Always probe /auth/me — session could be established via either localStorage
    // JWT (email/password login) OR httpOnly session_token cookie (Google OAuth).
    // httpOnly cookies aren't visible to JS, so we can't short-circuit on their absence.
    // The 401 on public/anonymous visitors is expected and harmless.
    try {
      const { data } = await client.get("/auth/me", {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(data);
    } catch {
      localStorage.removeItem("truck_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    localStorage.setItem("truck_token", data.access_token);
    setUser(data.user);
    // Fire GA4 login event via GTM dataLayer.
    try {
      const { trackConversion } = await import("../lib/analytics");
      trackConversion("login_success", {
        method: "email_password",
        user_id: data?.user?.id,
      });
    } catch {}
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await client.post("/auth/signup", payload);
    localStorage.setItem("truck_token", data.access_token);
    setUser(data.user);
    // Fire enriched conversion event (Meta Pixel + Google) with UTM attribution.
    // Pass user_id so eventID matches the server-side CAPI event → Meta dedupes.
    try {
      const { trackConversion } = await import("../lib/analytics");
      trackConversion("signup_complete", {
        content_name: "Fleet Account Signup",
        user_id: data?.user?.id,
      });
    } catch {}
    return data.user;
  };

  const logout = async () => {
    try { await client.post("/auth/logout", {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("truck_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshUser: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
